import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../api";

type SignedInUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
};

type EnterpriseSummary = { id: number; name: string; slug: string; updated_at?: string };
type CompletionTrendPoint = { month: string; label: string; completed: number };
type QueueStatus = {
  process_id: string;
  process_name: string;
  queue_id: string;
  queue_name: string;
  completed_in_period: number | null;
  completion_trend: CompletionTrendPoint[];
  not_started: number | null;
  waiting: number | null;
  in_process: number | null;
  unassigned: number | null;
  retrieved_at?: string | null;
  last_synced_at?: string | null;
  history_confidence?: string | null;
  current_confidence?: string | null;
};
type AssimilationPayload = {
  start_date: string;
  end_date: string;
  lens_id: string;
  queue_count: number;
  completed_in_period: number | null;
  history_missing_count?: number;
  not_started: number | null;
  queues: QueueStatus[];
};
type StrataBand = { population: number | null; entered_during_period: number | null; advanced_out_during_period: number | null; opportunity_to_next: number | null };
type StrataKey = "newcomer" | "relational_environment" | "bplanted" | "member";
type BadgeKey = "serve" | "give" | "dgroup";
type PopulationKey = StrataKey | BadgeKey;
type StrataContextMenu = { key: StrataKey; x: number; y: number };
type StrataPersonDetail = { person_id: string; display_name?: string | null; state: string; stratum: StrataKey | null; qualification_at?: string | null; evidence_confidence?: string; badges?: Record<BadgeKey, boolean> };
type StrataPayload = {
  data_status: string;
  person_snapshot_synced_at?: string | null;
  newcomer_definition_status: string;
  newcomer: { status: string; current_population: number | null; welcome_text_count: number | null; pastoral_connect_count: number | null; overlap_count: number | null; deduplicated_total: number | null; dated_entries_during_period: number | null; undated_or_inferred: number | null; opportunity_to_relational: number | null; unavailable_queue_ids: string[]; warnings: string[] };
  calculation: { membership_population_semantics?: string; inferred_dates?: string };
  strata: Record<StrataKey, StrataBand>;
  members: { active: number | null; funnel: number | null; other_active: number | null; engagement_opportunity: number | null; moved_into_membership_during_period: number | null; undated_funnel_members: number | null };
  badges: Record<BadgeKey, { available: boolean; status: string; window_start: string | null; window_end: string | null; warnings: string[]; total: number | null; funnel_members: number | null; other_active_members: number | null; opportunity_total: number | null; rate_all: number | null }>;
  details?: StrataPersonDetail[] | null;
};

const isoDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const monthRange = (anchor: Date) => ({
  start: isoDate(new Date(anchor.getFullYear(), anchor.getMonth(), 1)),
  end: isoDate(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)),
});
const displayTimestamp = (value?: string | null) =>
  value ? new Date(value).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }) : "Not available";

const strataCalculationInfo: Record<StrataKey, { title: string; summary: string; rules: string[] }> = {
  newcomer: {
    title: "Newcomer",
    summary: "People at the beginning of the assimilation funnel.",
    rules: [
      "Currently Not Started in Welcome Text or Pastoral Connect Calls — New to Bethel.",
      "A person appearing in both queues is counted once.",
      "Anyone qualified for a higher stratum is removed from Newcomer.",
    ],
  },
  relational_environment: {
    title: "Relational Environment",
    summary: "People who have reached a relational connection but have not advanced to bPlanted or Member.",
    rules: [
      "Attending Purple Book is In Process or Done, Pastoral Connect Call is Done, or Attended MTP is Done.",
      "Only one of those relational milestones is required.",
      "Anyone qualified for bPlanted or Member is removed from this stratum.",
    ],
  },
  bplanted: {
    title: "bPlanted",
    summary: "People in the membership-class phase who have not advanced to Member.",
    rules: [
      "Attended Class is Done or Schedule Leader Meeting is Done.",
      "Only one of those bPlanted milestones is required.",
      "Registered for Welcome Ceremony Done or current active membership removes the person from bPlanted.",
    ],
  },
  member: {
    title: "Member — not yet engaged",
    summary: "Current active Members who have no known Serve, Give, or dGroup engagement badge.",
    rules: [
      "Current membership type is Member and the Pushpay profile is active.",
      "Serve means checked in during the trailing three calendar months.",
      "Give or dGroup means a recorded gift or attendance during the trailing two calendar months.",
      "Members with any one of those badges are removed from this displayed Member opportunity count.",
    ],
  },
};

function QueueCompletionTrend({ points, queueName }: { points: CompletionTrendPoint[]; queueName: string }) {
  if (points.length === 0) {
    return <div style={{ color: "#fbbf24", fontSize: 10 }}>Six-month completion history is not available yet.</div>;
  }
  const width = 240;
  const height = 72;
  const left = 10;
  const right = width - 10;
  const top = 12;
  const bottom = height - 18;
  const maximum = Math.max(1, ...points.map((point) => point.completed));
  const coordinates = points.map((point, index) => ({
    ...point,
    x: points.length === 1 ? width / 2 : left + (index / (points.length - 1)) * (right - left),
    y: top + (1 - point.completed / maximum) * (bottom - top),
  }));
  const path = coordinates.map((point, index) => `${index === 0 ? "M" : "L"}${point.x.toFixed(1)},${point.y.toFixed(1)}`).join(" ");
  return <div style={{ display: "grid", gap: 2 }}>
    <div style={{ color: "#94a3b8", fontSize: 9, fontWeight: 900, textTransform: "uppercase" }}>Past 6 months · completed</div>
    <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={`${queueName} completed in each of the past 6 months`} style={{ width: "100%", height: 72, overflow: "visible" }}>
      <line x1={left} y1={bottom} x2={right} y2={bottom} stroke="#334155" strokeWidth="1" />
      <path d={path} fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
      {coordinates.map((point) => <g key={point.month}>
        <circle cx={point.x} cy={point.y} r="3" fill="#86efac" stroke="#052e16" strokeWidth="1" />
        <text x={point.x} y={Math.max(8, point.y - 6)} fill="#dcfce7" fontSize="8" fontWeight="800" textAnchor="middle">{point.completed}</text>
        <text x={point.x} y={height - 4} fill="#94a3b8" fontSize="8" textAnchor="middle">{point.label}</text>
      </g>)}
    </svg>
  </div>;
}

const navButton: React.CSSProperties = {
  height: 34,
  border: "1px solid transparent",
  borderRadius: 8,
  padding: "0 12px",
  color: "#e5e7eb",
  background: "transparent",
  fontSize: 13,
  fontWeight: 800,
  textDecoration: "none",
  display: "inline-flex",
  alignItems: "center",
};

export default function Assimilation({
  signedInUser,
  onSignOut,
}: {
  signedInUser?: SignedInUser | null;
  onSignOut?: () => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>([]);
  const [enterpriseId, setEnterpriseId] = React.useState<number | null>(() => {
    const queryValue = Number(searchParams.get("enterpriseId"));
    return Number.isFinite(queryValue) && queryValue > 0 ? queryValue : signedInUser?.enterprise_id ?? null;
  });
  const [month, setMonth] = React.useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [data, setData] = React.useState<AssimilationPayload | null>(null);
  const [strata, setStrata] = React.useState<StrataPayload | null>(null);
  const [strataError, setStrataError] = React.useState<string | null>(null);
  const [openPopulation, setOpenPopulation] = React.useState<PopulationKey | null>(null);
  const [strataContextMenu, setStrataContextMenu] = React.useState<StrataContextMenu | null>(null);
  const [calculationInfo, setCalculationInfo] = React.useState<StrataKey | null>(null);
  const [strataDetails, setStrataDetails] = React.useState<StrataPersonDetail[] | null>(null);
  const [strataDetailsLoading, setStrataDetailsLoading] = React.useState(false);
  const [strataDetailsError, setStrataDetailsError] = React.useState<string | null>(null);
  const [hoveredQueueId, setHoveredQueueId] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [liveError, setLiveError] = React.useState<string | null>(null);
  const [detailLoading, setDetailLoading] = React.useState<Set<string>>(() => new Set());
  const [error, setError] = React.useState<string | null>(null);
  const range = React.useMemo(() => monthRange(month), [month]);

  const showPopulationPeople = React.useCallback(async (key: PopulationKey) => {
    if (openPopulation === key) {
      setOpenPopulation(null);
      return;
    }
    setOpenPopulation(key);
    setStrataDetailsError(null);
    if (strataDetails || !enterpriseId) return;
    setStrataDetailsLoading(true);
    try {
      const response = await api.get(`/enterprises/${enterpriseId}/assimilation/strata`, {
        params: { start_date: range.start, end_date: range.end, include_details: true },
        timeout: 180000,
      });
      const payload = response.data as StrataPayload;
      setStrataDetails(payload.details || []);
    } catch (detailError) {
      const status = (detailError as { response?: { status?: number } }).response?.status;
      const detail = (detailError as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setStrataDetailsError(status === 403
        ? "You need sensitive integration access to view person names."
        : detail || "The person list could not be loaded.");
    } finally {
      setStrataDetailsLoading(false);
    }
  }, [enterpriseId, openPopulation, range.end, range.start, strataDetails]);

  React.useEffect(() => {
    let active = true;
    void api.get("/enterprises/").then((response) => {
      if (!active) return;
      const next = response.data as EnterpriseSummary[];
      setEnterprises(next);
      setEnterpriseId((current) => current ?? next[0]?.id ?? null);
    }).catch(() => setError("Could not load enterprises."));
    return () => { active = false; };
  }, []);

  const mergeLiveQueues = React.useCallback((live: { queues?: Array<Record<string, unknown>>; retrieved_at?: string; data_confidence?: string }) => {
    const byId = new Map((live.queues ?? []).map((queue) => [String(queue.id ?? ""), queue]));
    setData((current) => {
      if (!current) return current;
      const queues = current.queues.map((queue) => {
        const liveQueue = byId.get(queue.queue_id);
        if (!liveQueue) return queue;
        const counts = (liveQueue.status_counts ?? {}) as Record<string, unknown>;
        return {
          ...queue,
          not_started: counts.not_started == null ? queue.not_started : Number(counts.not_started),
          waiting: counts.waiting == null ? queue.waiting : Number(counts.waiting),
          in_process: counts.in_process == null ? queue.in_process : Number(counts.in_process),
          unassigned: liveQueue.unassigned_count === undefined || liveQueue.unassigned_count === null
            ? queue.unassigned
            : Number(liveQueue.unassigned_count || 0),
          retrieved_at: live.retrieved_at ?? queue.retrieved_at,
          current_confidence: live.data_confidence ?? queue.current_confidence,
        };
      });
      const allNotStartedLoaded = queues.every((queue) => queue.not_started !== null);
      return {
        ...current,
        queues,
        not_started: allNotStartedLoaded
          ? queues.reduce((total, queue) => total + Number(queue.not_started || 0), 0)
          : null,
      };
    });
  }, []);

  const loadLiveSummaries = React.useCallback(async (payload: AssimilationPayload, activeEnterpriseId: number) => {
    const processIds = Array.from(new Set(payload.queues.map((queue) => queue.process_id)));
    setLiveError(null);
    const results = await Promise.allSettled(processIds.map(async (processId) => {
      const response = await api.get(`/enterprises/${activeEnterpriseId}/assimilation/live`, {
        params: { process_id: processId },
        timeout: 60000,
      });
      mergeLiveQueues(response.data);
    }));
    if (results.some((result) => result.status === "rejected")) {
      setLiveError("Completion history is available, but one or more live queue-health refreshes failed. Retry by reloading the view.");
    }
  }, [mergeLiveQueues]);

  const load = React.useCallback(async () => {
    if (!enterpriseId) return;
    setIsLoading(true);
    setError(null);
    setOpenPopulation(null);
    setStrataDetails(null);
    setStrataDetailsError(null);
    try {
      const viewResponse = await api.get(`/enterprises/${enterpriseId}/assimilation`, {
        params: { start_date: range.start, end_date: range.end },
        timeout: 180000,
      });
      const payload = viewResponse.data as AssimilationPayload;
      setData(payload);
      setStrataError(null);
      void api.get(`/enterprises/${enterpriseId}/assimilation/strata`, {
        params: { start_date: range.start, end_date: range.end }, timeout: 180000,
      }).then((response) => setStrata(response.data as StrataPayload)).catch((strataLoadError) => {
        setStrata(null);
        const status = (strataLoadError as { response?: { status?: number; data?: { detail?: string } } }).response?.status;
        const detail = (strataLoadError as { response?: { data?: { detail?: string } } }).response?.data?.detail;
        setStrataError(status === 429
          ? "Pushpay is temporarily rate-limiting the person-level report. Reload this view in a moment."
          : detail || "The person-level Strata report could not be loaded.");
      }).finally(() => {
        // Strata needs a fully paginated people snapshot. Give it priority over
        // the optional per-process live queue refreshes to avoid exhausting the
        // shared Pushpay request budget before classification starts.
        void loadLiveSummaries(payload, enterpriseId);
      });
    } catch (loadError) {
      const detail = (loadError as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(detail || "Could not load Assimilation queue data.");
    } finally {
      setIsLoading(false);
    }
  }, [enterpriseId, loadLiveSummaries, range.end, range.start]);

  React.useEffect(() => { void load(); }, [load]);

  const openQueueDetails = React.useCallback((queue: QueueStatus) => {
    const key = `${queue.process_id}:${queue.queue_id}`;
    setHoveredQueueId(key);
    if (!enterpriseId || queue.unassigned !== null || detailLoading.has(key)) return;
    setDetailLoading((current) => new Set(current).add(key));
    void api.get(`/enterprises/${enterpriseId}/assimilation/live`, {
      params: { process_id: queue.process_id, queue_id: queue.queue_id },
      timeout: 60000,
    }).then((response) => mergeLiveQueues(response.data)).catch(() => {
      setLiveError("The queue cards loaded, but detailed live status could not be refreshed for one queue.");
    }).finally(() => {
      setDetailLoading((current) => {
        const next = new Set(current);
        next.delete(key);
        return next;
      });
    });
  }, [detailLoading, enterpriseId, mergeLiveQueues]);

  const grouped = React.useMemo(() => {
    const groups = new Map<string, QueueStatus[]>();
    (data?.queues ?? []).forEach((queue) => {
      if (!groups.has(queue.process_name)) groups.set(queue.process_name, []);
      groups.get(queue.process_name)!.push(queue);
    });
    return Array.from(groups.entries());
  }, [data]);
  const query = enterpriseId ? `?enterpriseId=${enterpriseId}` : "";

  return (
    <div style={{ minHeight: "100vh", background: "#05070b", color: "#f8fafc", padding: 16, fontFamily: "Inter, system-ui, sans-serif" }}>
      <header style={{ border: "1px solid rgba(148,163,184,.24)", borderRadius: 24, padding: 16, background: "linear-gradient(180deg,#0f172a,#080d14)", display: "grid", gap: 14, boxShadow: "0 18px 44px rgba(0,0,0,.36)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src={`${process.env.PUBLIC_URL}/favicon.png?v=2`} alt="Lenses logo" style={{ width: 54, height: 54 }} />
            <div><div style={{ color: "#22c55e", fontSize: 12, fontWeight: 900, letterSpacing: ".08em", textTransform: "uppercase" }}>Lenses</div><div style={{ fontSize: 18, fontWeight: 900 }}>Assimilation View</div></div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            {signedInUser ? <div style={{ fontSize: 12, color: "#cbd5e1", padding: "8px 10px", border: "1px solid rgba(148,163,184,.26)", borderRadius: 9 }}>{signedInUser.name}</div> : null}
            {onSignOut ? <button type="button" onClick={onSignOut} style={{ ...navButton, borderColor: "rgba(148,163,184,.26)", background: "#0f172a", cursor: "pointer" }}>Sign out</button> : null}
            <div aria-label="Workspace views" style={{ display: "flex", gap: 3, padding: 3, border: "1px solid rgba(148,163,184,.26)", borderRadius: 10, background: "#080d14" }}>
              <Link to={`/${query}`} style={navButton}>Map</Link><Link to={`/reports${query}`} style={navButton}>Summary</Link><Link to={`/dashboard${query}`} style={navButton}>Dashboard</Link>
              <span style={{ ...navButton, color: "#fff", borderColor: "rgba(34,197,94,.55)", background: "rgba(34,197,94,.18)" }}>Assimilation</span>
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "end", gap: 12, flexWrap: "wrap" }}>
          <label style={{ display: "grid", gap: 5, fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Enterprise<select value={enterpriseId ?? ""} onChange={(event) => { const next = Number(event.target.value); setEnterpriseId(next); setSearchParams({ enterpriseId: String(next) }); }} style={{ padding: "9px 11px", borderRadius: 9, background: "#080d14", color: "#fff", border: "1px solid #334155" }}>{enterprises.map((enterprise) => <option key={enterprise.id} value={enterprise.id}>{enterprise.name}</option>)}</select></label>
          <div style={{ display: "grid", gap: 5 }}><div style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase" }}>Reporting month</div><div style={{ display: "flex", alignItems: "center", gap: 8 }}><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))} style={{ ...navButton, borderColor: "#334155", background: "#080d14", cursor: "pointer" }}>‹</button><div style={{ minWidth: 150, textAlign: "center", fontWeight: 900 }}>{month.toLocaleDateString("en-US", { month: "long", year: "numeric" })}</div><button type="button" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))} style={{ ...navButton, borderColor: "#334155", background: "#080d14", cursor: "pointer" }}>›</button></div></div>
        </div>
      </header>

      {error ? <div role="alert" style={{ marginTop: 14, padding: 12, borderRadius: 10, color: "#fecaca", background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.35)" }}>{error}</div> : null}
      {liveError ? <div role="status" style={{ marginTop: 14, padding: 12, borderRadius: 10, color: "#fde68a", background: "rgba(245,158,11,.10)", border: "1px solid rgba(245,158,11,.32)" }}>{liveError}</div> : null}

      <main style={{ display: "grid", gap: 18, marginTop: 18 }}>
        <section aria-label="Assimilation Pipeline" style={{ display: "grid", gap: 12, padding: 16, border: "1px solid rgba(56,189,248,.35)", borderRadius: 18, background: "linear-gradient(145deg,#0c1824,#080d14)" }}>
          <div><h2 style={{ margin: 0, fontSize: 24 }}>Assimilation Pipeline</h2><div style={{ color: "#94a3b8", fontSize: 12 }}>Each person appears in their highest currently qualified stratum.</div></div>
          {strata ? <>
            <div style={{ display: "grid", gap: 7, justifyItems: "center" }}>
              {(["newcomer", "relational_environment", "bplanted", "member"] as const).map((key, index) => {
                const labels = { newcomer: "Newcomer", relational_environment: "Relational Environment", bplanted: "bPlanted", member: "Member" };
                const band = strata.strata[key];
                return <button type="button" key={key} title="Right-click for calculation details" aria-disabled={band.population === null} aria-expanded={openPopulation === key} onContextMenu={(event) => { event.preventDefault(); setStrataContextMenu({ key, x: event.clientX, y: event.clientY }); }} onClick={() => { setStrataContextMenu(null); if (band.population !== null) void showPopulationPeople(key); }} style={{ width: `${100 - index * 13}%`, minWidth: 260, padding: 12, borderRadius: 10, color: "#e2e8f0", cursor: band.population === null ? "default" : "pointer", background: `rgba(${56 + index * 35},189,248,.${10 + index * 3})`, border: `1px solid ${openPopulation === key ? "#7dd3fc" : "rgba(125,211,252,.28)"}`, display: "flex", justifyContent: "space-between", gap: 12, textAlign: "left" }}><strong>{labels[key]}</strong><span><strong>{band.population ?? "—"}</strong> current · {band.population === null ? "People unavailable" : openPopulation === key ? "Hide people" : "View people"}</span></button>;
              })}
            </div>
            {strataContextMenu ? <div role="menu" aria-label={`${strataCalculationInfo[strataContextMenu.key].title} options`} style={{ position: "fixed", left: Math.min(strataContextMenu.x, window.innerWidth - 230), top: Math.min(strataContextMenu.y, window.innerHeight - 70), zIndex: 50, width: 220, padding: 5, border: "1px solid #475569", borderRadius: 9, background: "#0f172a", boxShadow: "0 16px 36px rgba(0,0,0,.45)" }}><button type="button" role="menuitem" onClick={() => { setCalculationInfo(strataContextMenu.key); setStrataContextMenu(null); }} style={{ width: "100%", padding: "9px 10px", border: 0, borderRadius: 6, background: "transparent", color: "#e2e8f0", textAlign: "left", cursor: "pointer", fontWeight: 800 }}>How this is calculated</button></div> : null}
            {calculationInfo ? <div role="dialog" aria-modal="true" aria-label={`${strataCalculationInfo[calculationInfo].title} calculation`} onClick={() => setCalculationInfo(null)} style={{ position: "fixed", inset: 0, zIndex: 60, display: "grid", placeItems: "center", padding: 18, background: "rgba(2,6,23,.72)" }}><div onClick={(event) => event.stopPropagation()} style={{ width: "min(520px,100%)", padding: 18, border: "1px solid rgba(125,211,252,.38)", borderRadius: 16, background: "#0b1420", boxShadow: "0 24px 70px rgba(0,0,0,.55)" }}><div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 14 }}><div><div style={{ color: "#7dd3fc", fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>How this is calculated</div><h3 style={{ margin: "3px 0 0", fontSize: 21 }}>{strataCalculationInfo[calculationInfo].title}</h3></div><button type="button" aria-label="Close calculation details" onClick={() => setCalculationInfo(null)} style={{ border: "1px solid #475569", borderRadius: 7, background: "transparent", color: "#cbd5e1", padding: "4px 8px", cursor: "pointer" }}>Close</button></div><p style={{ color: "#cbd5e1", margin: "12px 0 9px" }}>{strataCalculationInfo[calculationInfo].summary}</p><ul style={{ color: "#94a3b8", margin: 0, paddingLeft: 20, display: "grid", gap: 7 }}>{strataCalculationInfo[calculationInfo].rules.map((rule) => <li key={rule}>{rule}</li>)}</ul></div></div> : null}
            {openPopulation ? <section aria-label={`${({ newcomer: "Newcomer", relational_environment: "Relational Environment", bplanted: "bPlanted", member: "Member opportunity", serve: "Serving", give: "Giving", dgroup: "D-group" } as const)[openPopulation]} people`} style={{ border: "1px solid #334155", borderRadius: 12, padding: 12, background: "rgba(15,23,42,.72)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", marginBottom: 8 }}><strong>{({ newcomer: "Newcomer", relational_environment: "Relational Environment", bplanted: "bPlanted", member: "Member opportunity", serve: "Serving", give: "Giving", dgroup: "D-group" } as const)[openPopulation]} people</strong><button type="button" onClick={() => setOpenPopulation(null)} style={{ border: "1px solid #475569", borderRadius: 7, background: "transparent", color: "#cbd5e1", padding: "4px 8px", cursor: "pointer" }}>Close</button></div>
              {strataDetailsLoading ? <div style={{ color: "#94a3b8" }}>Loading people…</div> : strataDetailsError ? <div role="alert" style={{ color: "#fbbf24" }}>{strataDetailsError}</div> : <div style={{ maxHeight: 360, overflowY: "auto", display: "grid", gap: 5 }}>
                {(strataDetails || []).filter((person) => {
                  if (person.state !== "active") return false;
                  if (["serve", "give", "dgroup"].includes(openPopulation)) return person.stratum === "member" && Boolean(person.badges?.[openPopulation as BadgeKey]);
                  if (openPopulation === "member") return person.stratum === "member" && !Object.values(person.badges || {}).some(Boolean);
                  return person.stratum === openPopulation;
                }).map((person) => <div key={person.person_id} style={{ display: "flex", justifyContent: "space-between", gap: 12, padding: "7px 9px", borderRadius: 7, background: "rgba(30,41,59,.7)" }}><span><strong>{person.display_name || `Person ${person.person_id}`}</strong><span style={{ color: "#64748b", marginLeft: 7, fontSize: 10 }}>ID {person.person_id}</span></span><span style={{ color: "#94a3b8", fontSize: 11 }}>{person.evidence_confidence || "undated"}</span></div>)}
                {strataDetails && !strataDetails.some((person) => {
                  if (person.state !== "active") return false;
                  if (["serve", "give", "dgroup"].includes(openPopulation)) return person.stratum === "member" && Boolean(person.badges?.[openPopulation as BadgeKey]);
                  if (openPopulation === "member") return person.stratum === "member" && !Object.values(person.badges || {}).some(Boolean);
                  return person.stratum === openPopulation;
                }) ? <div style={{ color: "#94a3b8" }}>No people are currently in this population.</div> : null}
              </div>}
            </section> : null}
            <div style={{ width: "61%", minWidth: 280, maxWidth: 1100, justifySelf: "center", display: "grid", gap: 9 }}>
              <div style={{ padding: 12, border: "1px solid #334155", borderRadius: 10, textAlign: "center" }}><strong>Active Members</strong><div>{strata.members.active} total</div><div>{strata.members.engagement_opportunity ?? "—"} not yet serving, giving, or in a D-group</div><div>{strata.members.moved_into_membership_during_period ?? "—"} moved in this period</div></div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 9 }}>
              {(["serve", "give", "dgroup"] as const).map((badge) => {
                const item = strata.badges[badge];
                const percentage = item.rate_all == null ? "—" : `${Math.round(item.rate_all * 100)}%`;
                return <button type="button" key={badge} disabled={!item.available} aria-expanded={openPopulation === badge} onClick={() => void showPopulationPeople(badge)} style={{ padding: 12, textAlign: "left", color: "#e2e8f0", background: "transparent", cursor: item.available ? "pointer" : "default", border: `1px solid ${openPopulation === badge ? "#7dd3fc" : item.status === "available" ? "rgba(34,197,94,.55)" : item.status === "partial" ? "rgba(245,158,11,.55)" : "#475569"}`, borderRadius: 10 }}><strong style={{ textTransform: "uppercase" }}>{badge}</strong>{item.available ? <><div><strong>{item.total}</strong> lit · {item.opportunity_total} opportunity · {percentage}</div><div>{item.funnel_members} Funnel · {item.other_active_members} Other · {openPopulation === badge ? "Hide people" : "View people"}</div><div style={{ color: "#94a3b8", fontSize: 10 }}>{item.window_start} through {item.window_end}</div>{item.status === "partial" ? <div style={{ color: "#fbbf24", fontSize: 10 }}>Partial data</div> : null}</> : <div style={{ color: "#fbbf24", fontSize: 11, marginTop: 5 }}>Badge data unavailable</div>}{item.warnings?.length ? <div style={{ color: "#94a3b8", fontSize: 9, marginTop: 4 }}>{item.warnings.join(" ")}</div> : null}</button>;
              })}
              </div>
            </div>
          </> : <div style={{ color: strataError ? "#fbbf24" : "#94a3b8" }}>{strataError || "Loading person-level Strata data…"} Queue measurements remain available below.</div>}
        </section>
        {isLoading ? <div style={{ color: "#94a3b8", padding: 24 }}>Loading live queue health and completion history…</div> : grouped.map(([processName, queues]) => (
          <section key={processName} style={{ display: "grid", gap: 10 }}>
            <h2 style={{ fontSize: 24, lineHeight: 1.2, margin: 0, color: "#e2e8f0", fontWeight: 950 }}>{processName}</h2>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(270px,1fr))", gap: 12 }}>
              {queues.map((queue) => {
                const queueKey = `${queue.process_id}:${queue.queue_id}`;
                const open = hoveredQueueId === queueKey;
                const loadingDetails = detailLoading.has(queueKey);
                return <article key={queueKey} tabIndex={0} onMouseEnter={() => openQueueDetails(queue)} onMouseLeave={() => setHoveredQueueId(null)} onFocus={() => openQueueDetails(queue)} onBlur={() => setHoveredQueueId(null)} style={{ position: "relative", minHeight: 205, borderRadius: 16, padding: 16, background: "linear-gradient(145deg,#101826,#0a1019)", border: `1px solid ${queue.not_started !== null && queue.not_started > 0 ? "rgba(245,158,11,.55)" : "rgba(34,197,94,.35)"}`, outline: "none" }}>
                  <div style={{ color: "#cbd5e1", fontSize: 14, fontWeight: 900, minHeight: 38 }}>{queue.queue_name}</div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", marginTop: 12 }}><div><div style={{ color: "#94a3b8", fontSize: 10, textTransform: "uppercase", fontWeight: 900 }}>Actual · completed</div><div style={{ fontSize: 36, fontWeight: 950 }}>{queue.completed_in_period ?? "—"}</div>{queue.completed_in_period === null ? <div style={{ color: "#fbbf24", fontSize: 10 }}>History sync pending</div> : null}</div><div style={{ color: queue.not_started !== null && queue.not_started > 0 ? "#fbbf24" : "#86efac", fontWeight: 900 }}>{queue.not_started === null ? "Refreshing…" : `${queue.not_started} not started`}</div></div>
                  <div style={{ marginTop: 12, color: "#64748b", fontSize: 11 }}>Hover or focus for live queue details</div>
                  {open ? <div style={{ position: "absolute", inset: 0, zIndex: 2, borderRadius: 16, padding: 14, background: "rgba(5,7,11,.97)", border: "1px solid #475569", boxShadow: "0 18px 44px rgba(0,0,0,.5)", display: "grid", gap: 5 }}><div style={{ fontWeight: 900, fontSize: 13 }}>{queue.queue_name}</div><QueueCompletionTrend points={queue.completion_trend ?? []} queueName={queue.queue_name} /><div style={{ display: "grid", gridTemplateColumns: "repeat(4,minmax(0,1fr))", gap: 5, fontSize: 10 }}>{[{ label: "Not Started", value: queue.not_started }, { label: "Unassigned", value: queue.unassigned }, { label: "Waiting", value: queue.waiting }, { label: "In Process", value: queue.in_process }].map((status) => <div key={status.label} style={{ display: "grid", gap: 1 }}><span style={{ color: "#94a3b8" }}>{status.label}</span><strong style={{ fontSize: 13 }}>{status.value ?? "…"}</strong></div>)}</div><div style={{ color: loadingDetails ? "#fbbf24" : "#64748b", fontSize: 9 }}>{loadingDetails ? "Refreshing detailed live status…" : <>Live: {displayTimestamp(queue.retrieved_at)} · History: {displayTimestamp(queue.last_synced_at)}</>}</div></div> : null}
                </article>;
              })}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
